export default class ChangeTracker {
  constructor(imageDataManager, frameSection, layerSection, imageResizer, selection, drawingToolKit, updateButtons, requestDraw, getChangeTrackers) {
    this._imageDataManager = imageDataManager;
    this._frameSection = frameSection;
    this._layerSection = layerSection;
    this._imageResizer = imageResizer;
    this._selection = selection;
    this._drawingToolKit = drawingToolKit;
    this._updateButtons = updateButtons;
    this._requestDraw = requestDraw;
    this._getChangeTrackers = getChangeTrackers;

    this._tracks = [];
    this._tracksIndex = 0;
  }

  static MAX_BYTE_SIZE = 640*640*4*2*4;

  static _lastId = 0;
  static _changeCount = 0;

  static get changeCount() {
    return this._changeCount;
  }
  static resetChangeCount() {
    this._changeCount = 0;
  }

  static _removeExceededTracks(changeTrackers) {
    while(true) {
      let byteSizeSum = 0;
      let oldestId = ChangeTracker._lastId + 1;
      let oldestChangeTracker = null;

      for(const changeTracker of changeTrackers) {
        byteSizeSum += changeTracker._tracks.reduce((sum, v) => sum + (v.byteSize ?? 4), 0);
        if(changeTracker._tracks.length > 0 && changeTracker._tracks[0].id < oldestId) {
          oldestId = changeTracker._tracks[0].id;
          oldestChangeTracker = changeTracker;
        }
      }

      if(byteSizeSum > ChangeTracker.MAX_BYTE_SIZE) {
        oldestChangeTracker._tracks.splice(oldestChangeTracker._tracksIndex);
        oldestChangeTracker._tracks.shift();
        oldestChangeTracker._tracksIndex = Math.max(0, oldestChangeTracker._tracksIndex - 1);
      } else {
        // デバッグ用ログ表示
        // const tracks = changeTrackers.reduce((acc, v, i) => acc.concat(v._tracks.map(v => {
        //   v.parent = i;
        //   return v;
        // })), []);
        // tracks.sort((a, b) => a.id - b.id);
        // tracks.forEach(v => console.log(v.id, v.byteSize, v.parent));
        // console.log('---');

        break;
      }
    }
  }

  get undoLength() {
    return this._tracksIndex;
  }
  get redoLength() {
    return this._tracks.length - this._tracksIndex;
  }
  get lastTrack() {
    return this._tracks.length ? this._tracks[this._tracksIndex - 1] : null;
  }

  clear() {
    this._tracks = [];
    this._tracksIndex = 0;
    this._updateButtons();
  }

  _pushTrack(track) {
    //-- トラックの現在位置以降を削除
    this._tracks.splice(this._tracksIndex);

    //--
    track.id = ++ChangeTracker._lastId;
    this._tracks.push(track);
    ChangeTracker._removeExceededTracks(this._getChangeTrackers());
    this._tracksIndex = this._tracks.length;
    ++ChangeTracker._changeCount;
    this._updateButtons();
  }

  do(name, ...args) {
    if(name.indexOf('selection') === -1) {
      this._selection.finishIfNot();
    }
    switch(name) {
    case 'pen-start':
      {
        let thick, force1px;
        [this._pixelColor, this._imageX, this._imageY, thick, force1px] = args;
        this._imageDataManager.preparePendingPen(this._pixelColor);
        this.do('pen-drag', this._imageX, this._imageY, thick, force1px);
      }
      break;
    case 'pen-drag':
      {
        const [imageX, imageY, thick, force1px] = args;
        this._imageDataManager.drawPendingPen(this._imageX, this._imageY, imageX, imageY, thick, force1px);
        [this._imageX, this._imageY] = [imageX, imageY];
      }
      break;
    case 'bucket-start':
      {
        let mode, includeDiagonal, distRGB, distA, arrowKeys; 
        [this._pixelColor, this._imageX, this._imageY, mode, includeDiagonal, distRGB, distA, arrowKeys] = args;
        this._imageDataManager.preparePending();
        switch(mode) {
        case 'normal':
          this._imageDataManager.fillPending(this._pixelColor, this._imageX, this._imageY, includeDiagonal, distRGB, distA);
          break;
        case 'addEdge':
          this._imageDataManager.fillPendingEdge(this._pixelColor, this._imageX, this._imageY, arrowKeys, false, false, distRGB, distA);
          break;
        case 'leaveEdge':
          this._imageDataManager.fillPendingEdge(this._pixelColor, this._imageX, this._imageY, arrowKeys, true, false, distRGB, distA);
          break;
        case 'eraseJaggies':
          this._imageDataManager.erasePendingJaggies(this._imageX, this._imageY, distRGB, distA);
          break;
        case 'replaceColor':
          {
            const srcColor = this._imageDataManager.getPixelColor(this._imageX, this._imageY);
            this._imageDataManager.replaceColorPending(this._pixelColor, srcColor, distRGB, distA);
          }
          break;
        }
      }
      break;
    case 'replace-color-all':
      {
        this._imageDataManager.cancelPending();
        const [distRGB, distA] = args;
        const dstColor = this._pixelColor;
        const srcColor = this._imageDataManager.getPixelColor(this._imageX, this._imageY);
        const arr = this._imageDataManager.replaceColorInAllFrames(dstColor, srcColor, distRGB, distA);
        if(arr) {
          const [oldCompactImageLists, byteSize] = arr;
          const {frameIdx, layerIdx} = this._imageDataManager;
          this._pushTrack({
            name,
            frameIdx, layerIdx,
            dstColor, srcColor, distRGB, distA, oldCompactImageLists,
            byteSize,
          });
          this._imageDataManager.reselect();
          this._frameSection.updateAllThumbnails();
          this._layerSection.updateAllThumbnails();
          this._requestDraw();
        }
      }
      break;
    case 'replace-color':
      {
        this._imageDataManager.cancelPending();
        const [distRGB, distA] = args;
        const dstColor = this._pixelColor;
        const {frameIdx, layerIdx} = this._imageDataManager;
        const srcColor = this._imageDataManager.getPixelColor(this._imageX, this._imageY);
        const arr = this._imageDataManager.replaceColorInFrame(frameIdx, dstColor, srcColor, distRGB, distA);
        if(arr) {
          const [oldCompactImages, byteSize] = arr;
          this._pushTrack({
            name,
            frameIdx, layerIdx,
            dstColor, srcColor, distRGB, distA, oldCompactImages,
            byteSize,
          });
          this._imageDataManager.reselect();
          this._layerSection.updateAllThumbnails();
          this._requestDraw();
        }
      }
      break;
    case 'shapes-start':
      {
        [this._pixelColor, this._imageX, this._imageY, this._thick, this._type] = args;
        this._imageDataManager.preparePending();
        this.do('shapes-drag', this._imageX, this._imageY);
      }
      break;
    case 'shapes-drag':
      {
        const [imageX, imageY] = args;
        this._imageDataManager.resetPending();
        let fill = true;
        switch(this._type) {
        case 'line':
          this._imageDataManager.drawPendingLine(this._pixelColor, this._imageX, this._imageY, imageX, imageY, this._thick);
          break;
        case 'rectStroke':
          fill = false;
        case 'rectFill':
          this._imageDataManager.drawPendingRect(this._pixelColor, this._imageX, this._imageY, imageX, imageY, this._thick, fill);
          break;
        case 'circleStroke':
          fill = false;
        case 'circleFill':
          this._imageDataManager.drawPendingCircle(this._pixelColor, this._imageX, this._imageY, imageX, imageY, this._thick, fill);
          break;
        case 'curve':
          this._imageDataManager.drawPendingCurve(this._pixelColor, this._imageX, this._imageY, imageX, imageY, imageX, imageY, this._thick);
          break;
        }
      }
      break;
    case 'shapes-nodrag-curve':
      {
        const [imageX, imageY, lastImageX, lastImageY] = args;
        this._imageDataManager.resetPending();
        this._imageDataManager.drawPendingCurve(this._pixelColor, this._imageX, this._imageY, imageX, imageY, lastImageX, lastImageY, this._thick);
      }
      break;
    case 'confirm-pending':
      {
        const [lastClickTime] = args;
        this._imageDataManager.confirmPending((frameIdx, layerIdx, pixels, oldPixelColors) => {
          this._pushTrack({
            name: 'confirm-pending',
            frameIdx, layerIdx, pixels, oldPixelColors,
            newPixelColor: this._pixelColor,
            lastClickTime: lastClickTime,
            byteSize: oldPixelColors.length + pixels.length * 4,
          });
        });
      }
      break;
    case 'cancel-pending':
      this._imageDataManager.cancelPending();
      break;
    case 'add-layer':
      {
        const [frameIdx, layerIdx, sz] = args;
        this._layerSection.add(layerIdx, sz);
        this._layerSection.select(layerIdx, sz);
        this._pushTrack({
          name,
          frameIdx, layerIdx, sz,
        });
      }
      break;
    case 'duplicate-layer':
      {
        const [frameIdx, layerIdx, sz] = args;
        this._layerSection.duplicate(layerIdx, sz);
        this._layerSection.select(layerIdx, sz);
        this._pushTrack({
          name,
          frameIdx, layerIdx, sz,
        });
      }
      break;
    case 'delete-layer':
      {
        const [frameIdx, layerIdx, sz] = args;
        const deleted = this._layerSection.delete(layerIdx, sz);
        const empty = this._layerSection.getDisplaySz() === 0;
        if(empty) this._layerSection.add(0, 1);
        this._layerSection.select2(layerIdx - 1);
        this._pushTrack({
          name,
          frameIdx, layerIdx, sz,
          deleted,
          empty,
          byteSize: deleted.reduce((sum, v) => sum + v.compactImage.byteSize(), 0),
        });
        this._imageDataManager.reselect();
      }
      break;
    case 'merge-layer':
      {
        const [frameIdx, layerIdx, sz] = args;
        const deleted = this._layerSection.merge(layerIdx, sz);
        this._layerSection.select(layerIdx, sz);
        this._pushTrack({
          name,
          frameIdx, layerIdx, sz,
          deleted,
          byteSize: deleted.reduce((sum, v) => sum + v.compactImage.byteSize(), 0),
        });
      }
      break;
    case 'move-layer':
      {
        const [frameIdx, srcIdx, srcSz, dstIdx] = args;
        this._layerSection.move(srcIdx, srcSz, dstIdx);
        const selectIdx = srcIdx < dstIdx ? dstIdx - srcSz : dstIdx;
        this._layerSection.select(selectIdx, srcSz);
        this._pushTrack({
          name,
          frameIdx, srcIdx, srcSz, dstIdx,
        });
      }
      break;
    case 'add-frame':
      {
        let [idx, sz] = args;
        this._frameSection.add(idx, sz);
        this._frameSection.select(idx, sz);
        this._pushTrack({
          name,
          idx, sz,
        });
      }
      break;
    case 'duplicate-frame':
      {
        let [idx, sz] = args;
        this._frameSection.duplicate(idx, sz);
        this._frameSection.select(idx, sz);
        this._pushTrack({
          name,
          idx, sz,
        });
      }
      break;
    case 'delete-frame':
      {
        const [idx, sz] = args;
        const deleted = this._frameSection.delete(idx, sz);
        const empty = this._frameSection.getDisplaySz() === 0;
        if(empty) this._frameSection.add(0, 1);
        this._frameSection.select2(idx - 1);
        this._pushTrack({
          name,
          idx, sz,
          deleted,
          empty,
          byteSize: deleted.reduce((sum, v) => sum + v.layerDataList.reduce(
            (sum, w) => sum + w.compactImage.byteSize(), 0
          ), 0),
        });
      }
      break;
    case 'move-frame':
      {
        let [srcIdx, srcSz, dstIdx] = args;
        this._frameSection.move(srcIdx, srcSz, dstIdx);
        const selectIdx = srcIdx < dstIdx ? dstIdx - srcSz : dstIdx;
        this._frameSection.select(selectIdx, srcSz);
        this._pushTrack({
          name,
          srcIdx, srcSz, dstIdx,
        });
      }
      break;
    case 'resize':
    case 'selection-resize-canvas':
      {
        const [x1, y1, x2, y2] = args;
        const [undoArgs, byteSize] = this._imageDataManager.resize(x1, y1, x2, y2);
        this._imageResizer.resize(x1, y1, x2, y2);
        this._imageDataManager.reselect();
        this._pushTrack({
          name: 'resize',
          redoArgs: args,
          undoArgs,
          byteSize,
        });
        if(name === 'selection-resize-canvas') this._imageDataManager.resetPending();
      }
      break;
    case 'selection-confirm':
      {
        const [clear] = args;
        const {changes, byteSize} = this._imageDataManager.confirmSelection(clear);
        if(changes.length > 0) {
          this._pushTrack({
            name: 'selection',
            changes,
            byteSize,
          });
        }
      }
      break;
    }
  }

  undo(tmpUndo = false) {
    if(this._tracksIndex === 0) return;
    if(!tmpUndo) this._selection.finishIfNot();
    this._drawingToolKit.finishIfNot();
    const track = this._tracks[--this._tracksIndex];
    ++ChangeTracker._changeCount;
    switch(track.name) {
    case 'replace-color-all':
      {
        const {frameIdx, layerIdx, oldCompactImageLists} = track;
        this._imageDataManager.replaceCompactImageLists(oldCompactImageLists);
        this._frameSection.select(frameIdx);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'replace-color':
      {
        const {frameIdx, layerIdx, oldCompactImages} = track;
        this._imageDataManager.replaceCompactImages(frameIdx, oldCompactImages);
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'confirm-pending':
      {
        const {frameIdx, layerIdx, oldPixelColors, pixels} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._imageDataManager.putPixelColors(layerIdx, oldPixelColors, pixels);
        if(!tmpUndo) this._layerSection.select(layerIdx);
      }
      break;
    case 'add-layer':
    case 'duplicate-layer':
      {
        const {frameIdx, layerIdx, sz} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.delete(layerIdx, sz);
        this._layerSection.select2(layerIdx - 1);
      }
      break;
    case 'delete-layer':
      {
        const {frameIdx, layerIdx, sz, deleted, empty} = track;
        if(empty) this._layerSection.delete(0, 1);
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.insert(layerIdx, ...deleted);
        this._layerSection.select(layerIdx, sz);
      }
      break;
    case 'merge-layer':
      {
        const {frameIdx, layerIdx, sz, deleted} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.delete(layerIdx, 1);
        this._layerSection.insert(layerIdx, ...deleted);
        this._layerSection.select(layerIdx, sz);
      }
      break;
    case 'move-layer':
      {
        const {frameIdx, srcIdx, srcSz, dstIdx} = track;
        this._frameSection.selectIfNot(frameIdx);
        if(srcIdx < dstIdx) {
          this._layerSection.move(dstIdx - srcSz, srcSz, srcIdx);
        } else {
          this._layerSection.move(dstIdx, srcSz, srcIdx + srcSz);
        }
        this._layerSection.select(srcIdx, srcSz);
      }
      break;
    case 'add-frame':
    case 'duplicate-frame':
      {
        const {idx, sz} = track;
        this._frameSection.delete(idx, sz);
        this._frameSection.select2(idx - 1);
      }
      break;
    case 'delete-frame':
      {
        const {idx, sz, deleted, empty} = track;
        if(empty) this._frameSection.delete(0, 1);
        this._frameSection.insert(idx, ...deleted);
        this._frameSection.select(idx, sz);
      }
      break;
    case 'move-frame':
      {
        const {srcIdx, srcSz, dstIdx} = track;
        if(srcIdx < dstIdx) {
          this._frameSection.move(dstIdx - srcSz, srcSz, srcIdx);
        } else {
          this._frameSection.move(dstIdx, srcSz, srcIdx + srcSz);
        }
        this._frameSection.select(srcIdx, srcSz);
      }
      break;
    case 'resize':
      {
        const [x1, y1, x2, y2, paramsList] = track.undoArgs;
        this._imageDataManager.undoResize(x1, y1, x2, y2, paramsList);
        this._imageResizer.resize(x1, y1, x2, y2);
        this._imageDataManager.reselect();
        this._requestDraw();
      }
      break;
    case 'selection':
      {
        const {changes} = track;
        for(const {frameIdx, layerIdx, orgImage} of changes) {
          this._imageDataManager.replaceCompactImage(frameIdx, layerIdx, orgImage);
        }
        this._imageDataManager.reselect();
        this._requestDraw();
      }
      break;
    }
    if(!tmpUndo) this._updateButtons();
  }

  redo() {
    if(this._tracksIndex === this._tracks.length) return;
    this._selection.cancel();
    this._drawingToolKit.cancelIfNotFinished();
    const track = this._tracks[this._tracksIndex++];
    ++ChangeTracker._changeCount;
    switch(track.name) {
    case 'replace-color-all':
      {
        const {frameIdx, layerIdx, dstColor, srcColor, distRGB, distA} = track;
        this._imageDataManager.replaceColorInAllFrames(dstColor, srcColor, distRGB, distA);
        this._frameSection.select(frameIdx);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'replace-color':
      {
        const {frameIdx, layerIdx, dstColor, srcColor, distRGB, distA} = track;
        this._imageDataManager.replaceColorInFrame(frameIdx, dstColor, srcColor, distRGB, distA);
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'confirm-pending':
      {
        const {frameIdx, layerIdx, newPixelColor, pixels} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._imageDataManager.putPixelColor(layerIdx, newPixelColor, pixels);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'add-layer':
      {
        const {frameIdx, layerIdx, sz} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.add(layerIdx, sz);
        this._layerSection.select(layerIdx, sz);
      }
      break;
    case 'duplicate-layer':
      {
        const {frameIdx, layerIdx, sz} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.duplicate(layerIdx, sz);
        this._layerSection.select(layerIdx, sz);
      }
      break;
    case 'delete-layer':
      {
        const {frameIdx, layerIdx, sz, empty} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.delete(layerIdx, sz);
        if(empty) this._layerSection.add(0, 1);
        this._layerSection.select2(layerIdx - 1);
      }
      break;
    case 'merge-layer':
      {
        const {frameIdx, layerIdx, sz} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.merge(layerIdx, sz);
        this._layerSection.select(layerIdx);
      }
      break;
    case 'move-layer':
      {
        const {frameIdx, srcIdx, srcSz, dstIdx} = track;
        this._frameSection.selectIfNot(frameIdx);
        this._layerSection.move(srcIdx, srcSz, dstIdx);
        const selectIdx = srcIdx < dstIdx ? dstIdx - srcSz : dstIdx;
        this._layerSection.select(selectIdx, srcSz);
        
      }
      break;
    case 'add-frame':
      {
        const {idx, sz} = track;
        this._frameSection.add(idx, sz);
        this._frameSection.select(idx, sz);
      }
      break;
    case 'duplicate-frame':
      {
        const {idx, sz} = track;
        this._frameSection.duplicate(idx, sz);
        this._frameSection.select(idx, sz);
      }
      break;
    case 'delete-frame':
      {
        const {idx, sz, empty} = track;
        this._frameSection.delete(idx, sz);
        if(empty) this._frameSection.add(0, 1);
        this._frameSection.select2(idx - 1);
      }
      break;
    case 'merge-frame':
      {
        const {idx, sz} = track;
        this._frameSection.merge(idx, sz);
        this._frameSection.select(idx);
      }
      break;
    case 'move-frame':
      {
        const {srcIdx, srcSz, dstIdx} = track;
        this._frameSection.move(srcIdx, srcSz, dstIdx);
        const selectIdx = srcIdx < dstIdx ? dstIdx - srcSz : dstIdx;
        this._frameSection.select(selectIdx, srcSz);
      }
      break;
    case 'resize':
      {
        const {redoArgs} = track;
        const [x1, y1, x2, y2] = redoArgs;
        this._imageDataManager.resize(x1, y1, x2, y2);
        this._imageResizer.resize(x1, y1, x2, y2);
        this._imageDataManager.reselect();
        this._requestDraw();
      }
      break;
    case 'selection':
      {
        const {changes} = track;
        for(const {frameIdx, layerIdx, newImage} of changes) {
          this._imageDataManager.replaceCompactImage(frameIdx, layerIdx, newImage);
        }
        this._imageDataManager.reselect();
        this._requestDraw();
      }
      break;
    }
    this._updateButtons();
  }
}
